import type { DayMark, DayPasses as DayPassesData, DaySegment } from '@tabel/shared/schemas/day-trace';

/*
 * «Проходы за день» в карточке дня на движке (MVP C4, макет «чек и полоса проходов» 12.10.2026; 003 — правая панель
 * до 620 px и телефон, research R13): легенда, полоса дня и компактная таблица отрезков с теми же номерами. Отрезки, засчитанное «от первого входа до последнего выхода», лишние
 * отметки и итоги собирает сервер (`engine.day`); здесь — только шкала и раскладка. Время на полосе — секунды от 00:00
 * даты по местному времени объекта: смена через полночь продолжает шкалу за 24:00, у полуночи — метка даты. Обед на
 * полосе не рисуется: он только в чеке.
 */

const HOUR = 3600;
const DAY = 86_400;
/** Отрезок уже этой доли полосы — номер над полосой, а не внутри (полоса панели ~430 px, номер ~18 px). */
const NARROW = 0.05;
/** Подпись потерянного прохода внутри области — от этой доли полосы. */
const WIDE_LABEL = 0.12;

const LOST: ReadonlySet<DaySegment['kind']> = new Set([
  'потерян_вход',
  'потерян_выход',
  'нет_выхода',
  'нет_входа',
]);

const AREA_LABEL: Partial<Record<DaySegment['kind'], string>> = {
  потерян_вход: '? вход не отмечен',
  потерян_выход: '? выход не отмечен',
  нет_выхода: 'нет выхода',
  нет_входа: 'нет входа',
};

interface Scale {
  readonly start: number;
  readonly end: number;
}

/**
 * Отрезок по ответу бригадира временем (Q-27, T071): секунды от 00:00 даты. Край `null` — от открытого прохода дня
 * («нет выхода» — от входа, «нет входа» — до выхода). `nextDay` — выход на следующие сутки: отрезок до конца полосы.
 */
export interface StatedSpan {
  readonly from: number | null;
  readonly to: number | null;
  readonly nextDay: boolean;
}

/** Открытый край дня, который заменяет ответ временем. */
const OPEN: ReadonlySet<DaySegment['kind']> = new Set(['нет_выхода', 'нет_входа']);

/**
 * Отрезок по ответу с обоими краями; края взять неоткуда — `null`. Открытого края нет (вопрос снят: пришёл проход,
 * T109) — от первого входа дня или до последнего выхода: время бригадира рядом с настоящим проходом.
 */
function resolveStated(day: DayPassesData, s: StatedSpan | null | undefined) {
  if (!s) return null;
  const starts = day.segments.map((x) => x.from).filter((x): x is number => x !== null);
  const ends = day.segments.map((x) => x.to).filter((x): x is number => x !== null);
  const from =
    s.from ??
    day.segments.find((x) => x.kind === 'нет_выхода')?.from ??
    (starts.length > 0 ? Math.min(...starts) : null);
  const to =
    s.to ??
    day.segments.find((x) => x.kind === 'нет_входа')?.to ??
    (ends.length > 0 ? Math.max(...ends) : null);
  if (from === null || to === null) return null;
  return { from, to, nextDay: s.nextDay };
}
type Stated = NonNullable<ReturnType<typeof resolveStated>>;

/**
 * Шкала: от часа первой отметки до часа после последней; у открытого края — запас. С ответом временем — и время
 * бригадира; выход на следующие сутки — до полуночи (дальше полоса не идёт: отрезок кончается знаком «→ след. день»).
 */
function scaleOf(day: DayPassesData, stated: Stated | null): Scale {
  const shown = stated ? day.segments.filter((s) => !OPEN.has(s.kind)) : day.segments;
  const points = [
    ...day.segments.flatMap((s) => [s.from, s.to]),
    ...day.counted.flatMap((c) => [c.from, c.to]),
    ...(stated ? [stated.from, stated.nextDay ? null : stated.to] : []),
  ].filter((x): x is number => x !== null);
  const min = Math.min(...points);
  const max = Math.max(...points);
  let start = Math.floor(min / HOUR) * HOUR;
  let end = Math.ceil(max / HOUR) * HOUR;
  if (end <= start) end = start + HOUR;
  if (shown.some((s) => s.to === null) && end - max < HOUR / 2) end += HOUR;
  if (shown.some((s) => s.from === null) && min - start < HOUR / 2) start -= HOUR;
  // Отрезок по ответу не прижат к краям полосы: запас в час, если до края меньше получаса.
  if (stated && stated.from - start < HOUR / 2) start -= HOUR;
  if (stated && !stated.nextDay && end - stated.to < HOUR / 2) end += HOUR;
  if (stated?.nextDay) end = Math.max(end, DAY);
  return { start, end };
}

/** Секунды от 00:00 → «чч:мм» (за полночью — время следующих суток). */
const clock = (sec: number) => {
  const m = Math.round((((sec % DAY) + DAY) % DAY) / 60);
  return `${two(Math.floor(m / 60))}:${two(m % 60)}`;
};

/** Штриховка отрезка «указано бригадиром»: светлый акцент через полосу. */
const HATCH =
  'bg-[repeating-linear-gradient(135deg,var(--color-accent-soft)_0_5px,var(--color-surface)_5px_9px)]';

const two = (n: number) => String(n).padStart(2, '0');

/** Метка часа: `чч:00`; у полуночи (не начало даты) — ещё дата `дд.мм`. */
function tickLabel(sec: number, date: string): { time: string; date: string | null } {
  const h = (((sec / HOUR) % 24) + 24) % 24;
  if (sec % DAY !== 0 || sec === 0) return { time: `${two(h)}:00`, date: null };
  const d = new Date(Date.parse(`${date}T00:00:00Z`) + sec * 1000).toISOString();
  return { time: '00:00', date: `${d.slice(8, 10)}.${d.slice(5, 7)}` };
}

function markTitle(m: DayMark | null): string | undefined {
  return m?.device ? `терминал ${m.device}` : undefined;
}

function Time({ mark }: { mark: DayMark | null }) {
  if (mark === null) return <span className="text-bad">нет прохода</span>;
  return <span title={markTitle(mark)}>{mark.time}</span>;
}

function NumBox({ seg }: { seg: DaySegment }) {
  const bad = seg.n === null;
  return (
    <span
      className={`inline-flex h-5 w-5 items-center justify-center rounded-[5px] text-[12px] font-semibold text-surface ${bad ? 'bg-bad' : 'bg-ok'}`}
      aria-label={bad ? 'нет прохода' : `отрезок ${seg.n}`}
    >
      {bad ? '?' : seg.n}
    </span>
  );
}

function Legend({ day, stated }: { day: DayPassesData; stated: Stated | null }) {
  const items: { key: string; box: string; text: string }[] = [];
  if (day.segments.some((s) => s.kind === 'пара' || s.kind === 'на_объекте')) {
    items.push({ key: 'on', box: 'bg-ok', text: 'на объекте' });
  }
  if (day.totals.away !== null) {
    items.push({ key: 'off', box: 'border border-line-strong bg-subtle', text: 'вне объекта' });
  }
  if (stated) {
    items.push({
      key: 'stated',
      box: `border-[1.5px] border-dashed border-accent ${HATCH}`,
      text: 'по вашему ответу',
    });
  }
  if (day.segments.some((s) => LOST.has(s.kind) && !(stated && OPEN.has(s.kind)))) {
    items.push({
      key: 'lost',
      box: 'border-[1.5px] border-dashed border-bad bg-warn-soft',
      text: 'нет прохода',
    });
  }
  return (
    <div className="ml-auto flex flex-wrap gap-x-3.5 gap-y-1 text-[12px] text-ink-2">
      {items.map((i) => (
        <span key={i.key} className="inline-flex items-center gap-1.5">
          <i className={`inline-block h-2.5 w-3.5 rounded-[2px] ${i.box}`} aria-hidden />
          {i.text}
        </span>
      ))}
    </div>
  );
}

/** Подписей шкалы не больше этого: в панели и на телефоне подпись «07:00» занимает ~32 px. */
const MAX_LABELS = 8;

/**
 * Подписан ли час `i` из `0…last`: крайние — всегда, между ними — через равный шаг, чтобы подписи не налезали
 * друг на друга; у последней — не ближе шага.
 */
function labeled(i: number, last: number): boolean {
  const every = Math.max(1, Math.ceil(last / MAX_LABELS));
  if (i === 0 || i === last) return true;
  return i % every === 0 && last - i >= every;
}

/**
 * Полоса дня: сетка по часам, отрезки с номерами, потерянные проходы, скобка «засчитано», шкала. С ответом временем —
 * отрезок «указано бригадиром» (пунктир со штриховкой) вместо открытого края «нет выхода» / «нет входа».
 */
function Strip({ day, date, stated }: { day: DayPassesData; date: string; stated: Stated | null }) {
  const { start, end } = scaleOf(day, stated);
  const span = end - start;
  const pos = (s: number) => `${(((s - start) / span) * 100).toFixed(3)}%`;
  const frac = (a: number, b: number) => (b - a) / span;
  const hoursOf: number[] = [];
  for (let h = start; h <= end; h += HOUR) hoursOf.push(h);
  const narrow = day.segments.some(
    (s) => s.n !== null && s.from !== null && s.to !== null && frac(s.from, s.to) < NARROW,
  );
  return (
    <div>
      {/* Подпись «засчитано» — строкой над скобкой: в узкой панели и на телефоне она переносится, а не обрезается. */}
      {day.counted.map((c) => (
        <div key={`${c.from}-${c.to}`} className="mb-4">
          <p className="text-center text-[12px] text-warn text-balance">
            {`засчитано ${c.duration} — от первого входа до последнего выхода`}
          </p>
          <div className="relative mt-1 h-2">
            <div
              className="absolute top-0 h-2 rounded-t-[4px] border-2 border-b-0 border-warn"
              style={{ left: pos(c.from), width: `${(frac(c.from, c.to) * 100).toFixed(3)}%` }}
            />
          </div>
        </div>
      ))}
      <div
        className={`relative h-[30px] rounded-[6px] border border-line bg-subtle ${narrow && day.counted.length === 0 ? 'mt-5' : 'mt-1'}`}
        data-testid="day-strip"
      >
        {hoursOf.slice(1, -1).map((h) => (
          <div
            key={h}
            className="absolute top-0 bottom-0 border-l border-dashed border-line-strong"
            style={{ left: pos(h) }}
          />
        ))}
        {stated &&
          (() => {
            const to = stated.nextDay ? end : stated.to;
            const f = frac(stated.from, to);
            const label = f >= WIDE_LABEL ? 'указано бригадиром' : '';
            return (
              <div
                data-kind="stated"
                title={`Указано бригадиром: ${clock(stated.from)} – ${clock(stated.to)}${stated.nextDay ? ' следующего дня' : ''}`}
                className={`absolute top-[3px] bottom-[3px] z-[1] flex items-center overflow-hidden rounded-[4px] border-[1.5px] border-dashed border-accent text-[12px] font-medium whitespace-nowrap text-accent ${HATCH} ${stated.nextDay ? 'justify-end rounded-r-none border-r-0 pr-1' : 'justify-center'}`}
                style={{ left: pos(stated.from), width: `${(f * 100).toFixed(3)}%` }}
              >
                {(label || stated.nextDay) && (
                  <span className="rounded-[3px] bg-surface/85 px-1">
                    {[label, stated.nextDay ? '→ след. день' : ''].filter(Boolean).join(' ')}
                  </span>
                )}
              </div>
            );
          })()}
        {day.segments.map((s, i) => {
          // Открытый край «нет выхода» / «нет входа» заменён отрезком по ответу.
          if (stated && OPEN.has(s.kind)) return null;
          const from = s.from ?? start;
          const to = s.to ?? end;
          const f = frac(from, to);
          const style = { left: pos(from), width: `${(f * 100).toFixed(3)}%` };
          const key = `${s.kind}-${i}`;
          const title = `${s.in?.time ?? '?'} – ${s.out?.time ?? '?'}`;
          if (LOST.has(s.kind)) {
            const label = AREA_LABEL[s.kind] ?? '?';
            return (
              <div
                key={key}
                title={title}
                data-kind={s.kind}
                className={`absolute top-[3px] bottom-[3px] flex items-center justify-center overflow-hidden rounded-[4px] border-[1.5px] border-dashed border-bad bg-warn-soft text-[12px] whitespace-nowrap text-bad ${s.from === null ? 'rounded-l-none border-l-0' : ''} ${s.to === null ? 'rounded-r-none border-r-0' : ''}`}
                style={style}
              >
                {f >= WIDE_LABEL ? label : '?'}
              </div>
            );
          }
          const inside = f >= NARROW;
          const text = s.kind === 'на_объекте' && f >= WIDE_LABEL ? `${s.n} · на объекте` : String(s.n);
          return (
            <div key={key}>
              <div
                title={title}
                data-kind={s.kind}
                className={`absolute top-[3px] bottom-[3px] flex items-center justify-center overflow-hidden rounded-[4px] bg-ok text-[12px] font-semibold whitespace-nowrap text-surface ${s.to === null ? 'rounded-r-none opacity-70' : ''}`}
                style={style}
              >
                {inside ? text : ''}
              </div>
              {!inside && (
                <span
                  className="absolute -top-[17px] -translate-x-1/2 text-[11px] font-semibold text-ok"
                  style={{ left: pos((from + to) / 2) }}
                  data-testid="above"
                >
                  {s.n}
                </span>
              )}
            </div>
          );
        })}
      </div>
      <div className="relative mt-1 h-4 text-[11px] text-ink-3 tabular-nums">
        {hoursOf.map((h, i) => {
          if (!labeled(i, hoursOf.length - 1)) return null;
          const t = tickLabel(h, date);
          const align = i === 0 ? '' : i === hoursOf.length - 1 ? '-translate-x-full' : '-translate-x-1/2';
          return (
            <span key={h} className={`absolute whitespace-nowrap ${align}`} style={{ left: pos(h) }}>
              {t.time}
              {t.date && <span className="ml-1 font-semibold text-ink-2">{t.date}</span>}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function Away({ seg }: { seg: DaySegment }) {
  if (seg.away === null) return <span className="text-ink-3">—</span>;
  if (seg.awayCounted) return <span className="text-warn">{`${seg.away} · засчитано`}</span>;
  return <span className="text-ink-2">{seg.away}</span>;
}

/** Ячейка таблицы отрезков: в узкой панели и на телефоне текст переносится, а не уходит вбок. */
const TD = 'border-t border-line px-2.5 py-1.5 align-top max-sm:px-2';

/**
 * Таблица отрезков: номер (квадрат цвета отрезка), вход, выход, на объекте, затем вне объекта; итого. Компактная —
 * по ширине правой панели (до 620 px, на телефоне 375 px) без прокрутки вбок: колонки в долях, длинные значения переносятся (003, R13).
 */
function SegmentsTable({ day }: { day: DayPassesData }) {
  const counted = day.totals.counted;
  return (
    <div className="mt-3 overflow-hidden rounded-control border border-line">
      <table data-compact className="w-full table-fixed border-separate border-spacing-0 text-[13px]">
        <colgroup>
          <col className="w-9" />
          <col className="w-[16%]" />
          <col className="w-[16%]" />
          <col />
          <col />
        </colgroup>
        <thead className="bg-subtle text-left text-[12px] leading-tight text-ink-3">
          <tr>
            <th className="px-2.5 py-1.5 font-normal max-sm:px-2">
              <span className="sr-only">Номер</span>
            </th>
            <th className="px-2.5 py-1.5 font-normal max-sm:px-2">Вход</th>
            <th className="px-2.5 py-1.5 font-normal max-sm:px-2">Выход</th>
            <th className="px-2.5 py-1.5 text-right font-normal max-sm:px-2">На объекте</th>
            <th className="px-2.5 py-1.5 text-right font-normal max-sm:px-2">Затем вне объекта</th>
          </tr>
        </thead>
        <tbody className="tabular-nums">
          {day.segments.map((s, i) => (
            <tr key={`${s.kind}-${i}`}>
              <td className={TD}>
                <NumBox seg={s} />
              </td>
              <td className={TD}>
                <Time mark={s.in} />
              </td>
              <td className={TD}>
                {s.kind === 'на_объекте' ? (
                  <span className="text-ink-3">на объекте</span>
                ) : (
                  <Time mark={s.out} />
                )}
              </td>
              <td className={`${TD} text-right whitespace-nowrap`}>
                {s.duration ?? <span className="text-ink-3">—</span>}
              </td>
              <td className={`${TD} text-right`}>
                <Away seg={s} />
              </td>
            </tr>
          ))}
          <tr className="bg-subtle font-semibold">
            <td className={TD} />
            <td colSpan={2} className={TD}>
              {counted === null ? 'Итого' : 'Итого по проходам'}
            </td>
            <td className={`${TD} text-right whitespace-nowrap`}>{day.totals.present ?? '—'}</td>
            <td className={`${TD} text-right font-normal`}>
              {counted !== null ? (
                <span className="text-warn">{`засчитано ${counted}`}</span>
              ) : (
                <span className={day.totals.away === null ? 'text-ink-3' : 'text-ink-2'}>
                  {day.totals.away ?? '—'}
                </span>
              )}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/**
 * «Проходы за день»: заголовок с легендой, полоса, таблица, убранные лишние отметки. `stated` — отрезок по ответу
 * бригадира временем (предпросмотр или записанный ответ, T071); часы по нему считает сервер.
 */
export function DayPasses({
  day,
  date,
  stated: statedSpan = null,
}: {
  day: DayPassesData;
  date: string;
  stated?: StatedSpan | null;
}) {
  const empty = day.segments.length === 0;
  const stated = resolveStated(day, statedSpan);
  return (
    <section aria-label="Проходы за день">
      <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1">
        <h3 className="text-[14px] font-semibold text-ink">Проходы за день</h3>
        {!empty && <Legend day={day} stated={stated} />}
      </div>
      {empty ? (
        <p className="text-ink-3">Проходов за день нет</p>
      ) : (
        <>
          <Strip day={day} date={date} stated={stated} />
          <SegmentsTable day={day} />
        </>
      )}
      {day.extra.length > 0 && (
        <p className="mt-2 text-[12px] text-ink-3">{`Лишние проходы не учтены: ${day.extra.join('; ')}.`}</p>
      )}
    </section>
  );
}
